import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Phone, ShieldCheck } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { http, apiError, normalizeIndianPhone } from "@/lib/api";
import { getAttribution, getFbCookies } from "@/lib/tracking/attribution";
import { newEventId, trackLeadFormStart, trackLeadSubmit } from "@/lib/tracking/tracker";
import { Field, Honeypot, PhoneInput, inputCls } from "./FormBits";


const validate = (f, t) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = t("err_name");
  if (!normalizeIndianPhone(f.phone)) e.phone = t("err_phone");
  return e;
};

export const LeadForm = ({ ctaName, position, startTracked = false, idPrefix = "lead" }) => {
  const { t, closeModal } = useSite();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [serverError, setServerError] = useState("");
  const started = useRef(startTracked);
  const eventIdRef = useRef(null);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onFocus = () => {
    if (started.current) return;
    started.current = true;
    trackLeadFormStart(ctaName, position);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (status === "submitting" || status === "done") return;
    const e = validate(form, t);
    setErrors(e);
    if (Object.keys(e).length) return;
    setStatus("submitting");
    setServerError("");
    eventIdRef.current = eventIdRef.current || newEventId();
    try {
      const { data } = await http.post("/leads", {
        ...form, phone: normalizeIndianPhone(form.phone), event_id: eventIdRef.current,
        attribution: { ...getAttribution(), cta_name: ctaName, cta_position: position },
        page_url: window.location.href, ...getFbCookies(),
      });
      if (!data.duplicate) trackLeadSubmit({ leadId: data.lead_id, eventId: eventIdRef.current, ctaName, position });
      setResult(data);
      setStatus("done");
      closeModal();
      const qs = new URLSearchParams({ lead: data.lead_id, name: form.name.trim().split(" ")[0], ...(data.duplicate ? { dup: "1" } : {}) });
      navigate(`/thank-you?${qs.toString()}`);
    } catch (err) {
      setServerError(apiError(err));
      setStatus("idle");
    }
  };

  if (status === "done") return null;

  const id = (k) => `${idPrefix}-${k}`;
  return (
    <form onSubmit={submit} onFocus={onFocus} noValidate data-testid={`${idPrefix}-form`} className="relative space-y-4">
      <Honeypot value={form.website} onChange={set("website")} />
      <Field label={t("full_name")} id={id("name")} error={errors.name}>
        <input id={id("name")} data-testid={`${idPrefix}-name-input`} autoComplete="name" value={form.name} maxLength={80}
          onChange={(e) => set("name")(e.target.value)} className={inputCls} placeholder={t("form_name_ph")} />
      </Field>
      <Field label={t("form_phone")} id={id("phone")} error={errors.phone}>
        <PhoneInput id={id("phone")} testId={`${idPrefix}-phone-input`} value={form.phone} onChange={set("phone")} />
      </Field>
      {serverError && <p role="alert" data-testid={`${idPrefix}-server-error`} className="rounded-xl bg-red-50 p-3 text-sm font-medium text-red-700">{serverError}</p>}
      <button type="submit" disabled={status === "submitting"} data-testid={`${idPrefix}-submit-button`}
        className="flex min-h-[56px] w-full items-center justify-center gap-2 rounded-full bg-forest text-base font-extrabold uppercase tracking-[0.02em] text-bone transition-[background-color,transform] hover:bg-forest-deep active:scale-[0.99] disabled:opacity-70">
        {status === "submitting" ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> : <Phone className="h-5 w-5" aria-hidden />}
        {status === "submitting" ? t("form_sending") : t("form_submit")}
      </button>
      <p data-testid={`${idPrefix}-helper-text`} className="-mt-1 text-center text-[15px] font-semibold text-forest">{t("form_helper")}</p>
      <p className="flex items-center justify-center gap-1.5 text-center text-sm text-sage"><ShieldCheck className="h-3.5 w-3.5" aria-hidden />{t("form_privacy")}</p>
    </form>
  );
};
